import type { Capability } from "@enduring-systems/entitlements";
import { useQuery } from "@tanstack/react-query";

import { getRegistry } from "./entitlements-api";

export interface UseRegistryReturn {
  capabilities: readonly Capability[];
  isLoading: boolean;
  error: string | null;
}

const registryKey = (): readonly unknown[] => ["entitlements", "registry"] as const;

/**
 * The capability registry.
 *
 * The registry is a deploy-time artefact rather than tenant data, so
 * `staleTime: Infinity` means it is read once per session: a remount paints the
 * cached list and sends nothing, which removes the second in-flight response
 * the counter existed to discard.
 */
export function useRegistry(): UseRegistryReturn {
  const query = useQuery({
    queryKey: registryKey(),
    queryFn: getRegistry,
    staleTime: Infinity,
  });

  return {
    capabilities: query.data ?? [],
    isLoading: query.isPending,
    error:
      query.error === null
        ? null
        : query.error instanceof Error
          ? query.error.message
          : "Failed to load capabilities",
  };
}
